import {
  CalendarIcon,
  CheckmarkIcon,
  CloudIcon,
  DatabaseIcon,
  GlobeIcon,
  ListNestedIcon,
  ListUlIcon,
  LonaIcons,
  NumberIcon,
  TextIcon,
} from "@tento-ui/ui/icons";
import { type JsonObject, type Option, RowId } from "@tento-lona/sheets";
import { ListDataSource, SourceRow } from "@tento-lona/sheets/platform";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import {
  DataJsonLegend,
  JsonHide,
  type JsonHideHost,
} from "./column-data-json-legend.ts";
import { liveVariantOf } from "../../sheet/live-variant.ts";

const SOURCE_DTYPES = ["text", "number", "boolean", "obj"] as const;
const SOURCE_CARDINALITIES = [
  "scalar",
  "series",
  "tensor",
  "per-cell",
] as const;
const SOURCE_CELL_CARDINALITIES = ["single", "multi"] as const;

function sourceDescriptorIds(): readonly string[] {
  const ids: string[] = [];
  for (const dtype of SOURCE_DTYPES) {
    for (const cardinality of SOURCE_CARDINALITIES) {
      for (const cellCardinality of SOURCE_CELL_CARDINALITIES) {
        ids.push(`source.${dtype}.${cardinality}.${cellCardinality}`);
      }
    }
  }
  return ids;
}

const SOURCE_DESCRIPTOR_IDS = sourceDescriptorIds();
const DATA_SOURCE_CHROME_PLUGIN_ID = "source.text.scalar.single";

/**
 * The source-row plugin for a scalar data row's chrome: its label and value
 * legend.
 */
export class DataChromeSourcePlugin implements RenderPlugin.SourceRow<
  HTMLElement,
  DataJsonLegend
> {
  readonly role = "source";
  readonly id: "source.text.scalar.single" = DATA_SOURCE_CHROME_PLUGIN_ID;
  readonly aliases: readonly string[] = SOURCE_DESCRIPTOR_IDS.filter((id) =>
    id !== DATA_SOURCE_CHROME_PLUGIN_ID
  );
  readonly icon: HTMLTemplateElement = LonaIcons.$template(DatabaseIcon);
  readonly #listIcon: HTMLTemplateElement = LonaIcons.$template(
    ListUlIcon,
  );

  // A source row's icon follows its implied shape first (the typed
  // identity stamped in `meta.shape`), then its scalar dtype. Mirrors
  // the `RENDERER_BY_SHAPE` index in `render-plugin-core` so the row's
  // icon and its renderer agree. Templates are minted once.
  readonly #byShape: ReadonlyMap<string, HTMLTemplateElement> = new Map([
    ["task/v1", LonaIcons.$template(CheckmarkIcon)],
    ["event/v1", LonaIcons.$template(CalendarIcon)],
    ["weather/v1", LonaIcons.$template(CloudIcon)],
    ["timezone/v1", LonaIcons.$template(GlobeIcon)],
    ["logs/v1", LonaIcons.$template(ListNestedIcon)],
  ]);
  readonly #textIcon: HTMLTemplateElement = LonaIcons.$template(TextIcon);
  readonly #numberIcon: HTMLTemplateElement = LonaIcons.$template(
    NumberIcon,
  );
  readonly #checkmarkIcon: HTMLTemplateElement = LonaIcons.$template(
    CheckmarkIcon,
  );

  constructor(
    readonly makeRowOptions: RowOptionsElement.Factory = RowOptionsElement.Factory.NOOP,
    patchRowAttributes: Option<
      (rowId: RowId, attrs: JsonObject) => void
    >,
  ) {
    // Legend with the raw-JSON eye toggle. Source rows resolve their chrome
    // through this plugin (not the data-json renderer), so the toggle for a
    // concrete obj-dtype source row — one that renders its own JSON cells —
    // is hosted here. Non-obj rows and hosts with no attribute-patch channel
    // get the plain label (the toggle hides itself via `canHide`).
    const host: JsonHideHost = {
      // Gate on the wire type (synchronous), not `row.variant` — the variant
      // hydrates after the legend's first bind and legends don't rebind on
      // its arrival, so a variant-based gate would leave the eye hidden.
      // `data`/`data-json` are exactly the rows that render raw JSON cells.
      canHide: (row) =>
        patchRowAttributes != null &&
        (row.descriptor.id === "data" || row.descriptor.id === "data-json"),
      isHidden: (row) =>
        JsonHide.isHiddenAttrs(row.attributes()),
      setHidden: (row, hidden) => {
        patchRowAttributes?.(
          RowId.fromLocalId(row.id),
          JsonHide.attrsFor(hidden ? "hidden" : "visible"),
        );
      },
    };
    this.legendFactory = DataJsonLegend.makeFactory(host);
  }

  iconForRow(row: SheetRow.Joined.Handle): HTMLTemplateElement {
    const variant = liveVariantOf(row);
    if (variant instanceof SourceRow) {
      const shape = variant.shape;
      const byShape = typeof shape === "string"
        ? this.#byShape.get(shape)
        : undefined;
      if (byShape) return byShape;
      switch (variant.descriptor.dtype) {
        case "text":
          return this.#textIcon;
        case "number":
          return this.#numberIcon;
        case "boolean":
          return this.#checkmarkIcon;
      }
    }
    return variant instanceof ListDataSource ? this.#listIcon : this.icon;
  }

  makeCell(): HTMLElement {
    return document.createElement("div");
  }

  bindCell(_$cell: HTMLElement, _context: BindCellContext): void {}
  onResize(_$cell: HTMLElement, _context: BindCellContext): void {}

  loadData = null;
  readonly legendFactory: SheetLegendClass<DataJsonLegend>;

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}
